import { getDeviceInfo } from './deviceInfo';
import { buildApiUrl } from '@/api/baseUrl';
import { getAccessToken } from '@/api/client';

interface TrackPayload {
  event_name: string;
  properties?: Record<string, string | number | boolean | null>;
}

type GenerateTrackPayload = {
  template?: string | null;
  style?: string | null;
  model?: string;
  ratio?: string | null;
  mode?: string;
  entry_id?: string;
  generation_operation?: string;
  has_reference?: boolean;
  image_count?: number;
  source?: string;
};

type HomeEntryPayload = {
  entry_id: string;
  entry_name?: string;
  source?: string;
};

type StandardGenerationPayload = {
  entry_id?: string;
  entry_name?: string;
  tab_name?: string;
  mode?: string;
  mode_name?: string;
  model?: string;
  quality?: string;
  generation_operation?: string;
  source?: string;
  has_reference?: boolean;
  image_count?: number;
};

async function send(payload: TrackPayload) {
  const device = getDeviceInfo();
  try {
    await fetch(buildApiUrl('/app/analytics/event'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAccessToken() ?? ''}`,
      },
      body: JSON.stringify({
        ...payload,
        device_id: device.device_id,
        platform: device.platform,
        timestamp: new Date().toISOString(),
      }),
      keepalive: true,
    });
  } catch {
    // analytics failures are silent
  }
}

export const track = {
  pageView: (page: string) => send({ event_name: 'page_view', properties: { page } }),
  homeView: () => send({ event_name: 'home_view', properties: { page: 'home' } }),
  homeEntryExposure: (p: HomeEntryPayload) =>
    send({ event_name: 'home_entry_exposure', properties: p }),
  homeEntryClick: (p: HomeEntryPayload) =>
    send({ event_name: 'home_entry_click', properties: p }),
  generatePageView: (p: StandardGenerationPayload = {}) =>
    send({ event_name: 'generate_page_view', properties: p }),
  generateTabClick: (p: StandardGenerationPayload) =>
    send({ event_name: 'generate_tab_click', properties: p }),
  homeCtaClick: (source: string) => send({ event_name: 'home_cta_click', properties: { source } }),
  loginSuccess: (method: 'phone' | 'email' | 'google' | 'apple') => send({ event_name: 'login_success', properties: { method } }),
  referenceUploadSuccess: (p: { source: string; count?: number }) =>
    send({ event_name: 'reference_upload_success', properties: p }),
  referenceUploadFail: (p: { source: string; reason?: string | null }) =>
    send({ event_name: 'reference_upload_fail', properties: p }),
  generateStart: (p: GenerateTrackPayload) =>
    send({ event_name: 'generate_start', properties: p }),
  generateSubmit: (p: StandardGenerationPayload) =>
    send({ event_name: 'generate_submit', properties: p }),
  generationSuccess: (genId: string, p: StandardGenerationPayload = {}) =>
    send({ event_name: 'generation_success', properties: { gen_id: genId, ...p } }),
  generationFailed: (reasonCode: string, p: StandardGenerationPayload = {}) =>
    send({ event_name: 'generation_failed', properties: { reason_code: reasonCode, ...p } }),
  generationBlocked: (reasonCode: string, p: StandardGenerationPayload = {}) =>
    send({ event_name: 'generation_blocked', properties: { reason_code: reasonCode, ...p } }),
  generateSuccess: (genId: string, p: Record<string, string | number | boolean | null> = {}) =>
    send({ event_name: 'generate_success', properties: { gen_id: genId, ...p } }),
  generateFail: (reason: string, p: Record<string, string | number | boolean | null> = {}) =>
    send({ event_name: 'generate_fail', properties: { reason, ...p } }),
  resultAction: (action: string, genId?: string, source = 'result_card') =>
    send({ event_name: 'result_action_click', properties: { action, gen_id: genId ?? null, source } }),
  inspirationOpen: () => send({ event_name: 'inspiration_open' }),
  shareOpen: () => send({ event_name: 'share_open' }),
  shareSuccess: (source: string, genId?: string) =>
    send({ event_name: 'share_success', properties: { source, gen_id: genId ?? null } }),
  saveSuccess: (quality: 'sd' | 'hd', genId?: string) =>
    send({ event_name: 'result_save_success', properties: { quality, gen_id: genId ?? null } }),
  imageSaveSuccess: (p: {
    source: string;
    genId?: string;
    quality?: 'sd' | 'hd';
    method?: string;
    stage?: string;
    durationMs?: number;
    bytes?: number;
    httpStatus?: number;
    urlType?: string;
  }) =>
    send({
      event_name: 'image_save_success',
      properties: {
        source: p.source,
        gen_id: p.genId ?? null,
        quality: p.quality ?? 'sd',
        method: p.method ?? null,
        stage: p.stage ?? null,
        duration_ms: p.durationMs ?? null,
        bytes: p.bytes ?? null,
        http_status: p.httpStatus ?? null,
        url_type: p.urlType ?? null,
      },
    }),
  imageSaveFail: (p: {
    source: string;
    genId?: string;
    stage?: string;
    quality?: 'sd' | 'hd';
    method?: string;
    durationMs?: number;
    bytes?: number;
    httpStatus?: number;
    urlType?: string;
  }) =>
    send({
      event_name: 'image_save_fail',
      properties: {
        source: p.source,
        gen_id: p.genId ?? null,
        stage: p.stage ?? 'unknown',
        quality: p.quality ?? 'sd',
        method: p.method ?? null,
        duration_ms: p.durationMs ?? null,
        bytes: p.bytes ?? null,
        http_status: p.httpStatus ?? null,
        url_type: p.urlType ?? null,
      },
    }),
  favoriteToggle: (favorited: boolean, genId?: string) =>
    send({ event_name: 'result_favorite_toggle', properties: { favorited, gen_id: genId ?? null } }),
  vipSheetView: (source: string) => send({ event_name: 'membership_popup_view', properties: { source } }),
  vipUpgradeClick: (source: string) => send({ event_name: 'vip_upgrade_click', properties: { source } }),
  orderClick: (source: string, packageId?: string | null) =>
    send({ event_name: 'order_click', properties: { source, package_id: packageId ?? null } }),
  paymentSuccess: (source: string, packageId?: string | null, amount?: number | null) =>
    send({ event_name: 'payment_success', properties: { source, package_id: packageId ?? null, amount: amount ?? null } }),
};
